import {
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import React, { forwardRef, useEffect, useState } from 'react';
import {
  useFindAllHardwareForUserQuery,
  useFindUserForUserQuery,
  useSendDepartmentEmailMutation,
} from '../../api/api-hooks';
import CompanyHardwareTable from './CompanyHardwareTable';
import DownloadLicensesForm from './DownloadLicensesForm';
import UtilitiesWidget from './UtilitiesWidget';
import { Department, Hardware } from '../../api/api-generated';
import { buildEmail } from './utils/emailBuilders';

const useStyles = makeStyles((theme: Theme) => ({
  alert: {
    marginTop: theme.spacing(8),
  },
}));

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant='filled' {...props} />;
});

export interface HardwareForForm
  extends Omit<Hardware, 'user' | 'companies' | 'order' | 'department'> {
  department: DepartmentForForm | null;
}

export type DepartmentForForm =
  | Department
  | { id: number; departmentName: string };

const Dashboard = () => {
  const { data: profile } = useFindUserForUserQuery({});
  const { data: hardware } = useFindAllHardwareForUserQuery({});
  const [editMode, setEditMode] = useState(false);
  const [alertOpen, setAlertOpen] = useState<boolean>(true);
  const [submitted, setSubmitted] = useState(false);
  const [sendEmail] = useSendDepartmentEmailMutation();
  const [formHardware, setFormHardware] = useState<HardwareForForm[]>([]);
  useEffect(() => {
    if (hardware) setFormHardware(hardware);
    setSubmitted(false);
  }, [hardware, editMode]);
  const classes = useStyles();
  if (profile === undefined) return <CircularProgress />;
  const handleClose = (event?: any, reason?: string) => {
    if (reason === 'clickaway') return;
    setAlertOpen(false);
  };

  const handleSubmit = async () => {
    if (!profile || !formHardware) return;
    setSubmitted(true);
    const email = buildEmail(profile, formHardware);
    await sendEmail({ sendDepartmentEmailDto: { message: email } });
  };

  return (
    <Grid container direction='column' alignItems='flex-start' spacing={2}>
      <Grid item>
        <Typography variant='h2'>{profile.company.companyName}</Typography>
      </Grid>
      <Grid container item alignItems='center' spacing={1}>
        <Grid item>
          <DownloadLicensesForm />
        </Grid>
        <Grid item>
          <UtilitiesWidget />
        </Grid>
        {!editMode && !submitted && (
          <Grid item>
            <Button
              variant='outlined'
              onClick={() => setEditMode((state) => !state)}
            >
              Assign Departments
            </Button>
          </Grid>
        )}
        {editMode && !submitted && (
          <>
            <Grid item>
              <Button variant='outlined' onClick={handleSubmit}>
                Save Departments
              </Button>
            </Grid>
            <Grid item>
              <Button
                color='warning'
                variant='outlined'
                onClick={() => setEditMode((state) => !state)}
              >
                Cancel
              </Button>
            </Grid>
          </>
        )}
        {submitted && (
          <>
            <Grid item>
              <Button
                color='warning'
                variant='outlined'
                onClick={() => setEditMode((state) => !state)}
              >
                Close Edit Mode
              </Button>
            </Grid>
            <Grid item>
              <Typography color='error'>
                Thank you! Your changes have been submitted. LP Technologies
                will process your proposed changes.
              </Typography>
            </Grid>
          </>
        )}
      </Grid>
      <Grid item style={{ width: '100%' }}>
        <CompanyHardwareTable
          editMode={editMode}
          formHardware={formHardware}
          setFormHardware={setFormHardware}
        />
      </Grid>
      <Snackbar
        open={alertOpen}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        className={classes.alert}
      >
        <Alert onClose={handleClose} severity='info' variant='outlined'>
          Select software from the drop-down dialog to download licenses.
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default Dashboard;
